import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/toaster'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Loader2, Camera, Key, Bell, Palette, Building } from 'lucide-react'
import { cn } from '@/lib/utils'

type SettingsTab = 'profile' | 'security' | 'notifications' | 'appearance' | 'workspace'

export default function SettingsPage() {
    const { user, tenant, fetchUser } = useAuthStore()
    const [activeTab, setActiveTab] = useState<SettingsTab>('profile')
    const [name, setName] = useState(user?.name || '')
    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')

    const updateProfileMutation = useMutation({
        mutationFn: (data: { name: string }) => api.patch(`/users/${user?.id}`, data),
        onSuccess: () => { fetchUser(); toast({ title: 'Profile updated' }) },
        onError: (error: any) => { toast({ title: 'Update failed', description: error.message, variant: 'destructive' }) },
    })

    const changePasswordMutation = useMutation({
        mutationFn: (data: { currentPassword: string; newPassword: string }) =>
            api.post(`/users/${user?.id}/change-password`, data),
        onSuccess: () => { setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); toast({ title: 'Password changed' }) },
        onError: (error: any) => { toast({ title: 'Failed', description: error.message, variant: 'destructive' }) },
    })

    const tabs = [
        { id: 'profile', label: 'Profile', icon: Avatar },
        { id: 'security', label: 'Security', icon: Key },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'appearance', label: 'Appearance', icon: Palette },
        { id: 'workspace', label: 'Workspace', icon: Building },
    ]

    return (
        <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">Settings</h1>

            <div className="flex gap-8">
                <nav className="w-48 space-y-1">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as SettingsTab)}
                            className={cn("w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors",
                                activeTab === tab.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"
                            )}
                        >
                            {tab.id === 'profile' ? <Avatar className="h-4 w-4"><AvatarFallback className="text-[8px]">{user?.name?.charAt(0)}</AvatarFallback></Avatar> : <tab.icon className="h-4 w-4" />}
                            {tab.label}
                        </button>
                    ))}
                </nav>

                <div className="flex-1 border rounded-lg p-6">
                    {activeTab === 'profile' && (
                        <div className="space-y-6">
                            <h2 className="text-lg font-semibold">Profile</h2>
                            <div className="flex items-center gap-4">
                                <div className="relative">
                                    <Avatar className="h-20 w-20">
                                        <AvatarImage src={user?.avatarUrl || undefined} />
                                        <AvatarFallback className="text-2xl">{user?.name?.charAt(0)}</AvatarFallback>
                                    </Avatar>
                                    <button className="absolute bottom-0 right-0 p-1.5 bg-primary text-white rounded-full shadow">
                                        <Camera className="h-3 w-3" />
                                    </button>
                                </div>
                                <div><p className="font-medium">{user?.name}</p><p className="text-sm text-muted-foreground">{user?.email}</p></div>
                            </div>
                            <form onSubmit={(e) => { e.preventDefault(); updateProfileMutation.mutate({ name }) }} className="space-y-4">
                                <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
                                <div><Label>Email</Label><Input value={user?.email} disabled className="bg-muted" /></div>
                                <Button type="submit" disabled={updateProfileMutation.isPending}>
                                    {updateProfileMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Changes
                                </Button>
                            </form>
                        </div>
                    )}

                    {activeTab === 'security' && (
                        <div className="space-y-6">
                            <h2 className="text-lg font-semibold">Security</h2>
                            <form onSubmit={(e) => {
                                e.preventDefault()
                                if (newPassword !== confirmPassword) { toast({ title: 'Passwords do not match', variant: 'destructive' }); return }
                                changePasswordMutation.mutate({ currentPassword, newPassword })
                            }} className="space-y-4">
                                <div><Label>Current Password</Label><Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} /></div>
                                <div><Label>New Password</Label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} /></div>
                                <div><Label>Confirm Password</Label><Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} /></div>
                                <Button type="submit" disabled={changePasswordMutation.isPending}>
                                    {changePasswordMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Change Password
                                </Button>
                            </form>
                        </div>
                    )}

                    {activeTab === 'notifications' && (
                        <div className="space-y-6">
                            <h2 className="text-lg font-semibold">Notifications</h2>
                            <p className="text-muted-foreground">Notification settings coming soon.</p>
                        </div>
                    )}

                    {activeTab === 'appearance' && (
                        <div className="space-y-6">
                            <h2 className="text-lg font-semibold">Appearance</h2>
                            <div className="space-y-4">
                                <Label>Theme</Label>
                                <div className="flex gap-2">
                                    <Button variant="outline" className="flex-1">Light</Button>
                                    <Button variant="default" className="flex-1">Dark</Button>
                                    <Button variant="outline" className="flex-1">System</Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'workspace' && (
                        <div className="space-y-6">
                            <h2 className="text-lg font-semibold">Workspace</h2>
                            <div className="space-y-4">
                                <div><Label>Organization Name</Label><Input value={tenant?.name} disabled className="bg-muted" /></div>
                                <div><Label>Plan</Label><div className="flex items-center gap-2"><span className="px-2 py-1 bg-primary/10 text-primary rounded capitalize">{tenant?.plan}</span></div></div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
